import React, { useEffect, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { api } from '../services/api';
import { auth } from '../services/auth';
import { deviceNotes, deviceHasNotes } from '../utils/deviceLampActions';

/**
 * Operator notes for one lamp: editable with device_metadata:write, read-only otherwise.
 * onSaved receives { notes, notes_updated_at, notes_updated_by } ('' when cleared).
 */
// buttonSx (optional) restyles Save / Clear, e.g. to match the map popup.
const DeviceNotesEditor = ({ device, onSaved, minRows = 2, maxRows = 6, stopPropagation = false, label = null, buttonSx = null }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [draft, setDraft] = useState(device?.notes || '');
  const [saving, setSaving] = useState(false);
  const canEdit = auth.canWriteDeviceMetadata();
  const serial = device?.serial_number;

  useEffect(() => {
    setDraft(device?.notes || '');
  }, [serial, device?.notes]);

  const stop = (e) => {
    if (stopPropagation) e.stopPropagation();
  };

  const save = async (value) => {
    if (!serial || saving) return;
    const notes = value.trim();
    setSaving(true);
    try {
      const res = await api.patchDeviceMetadata(serial, { notes });
      // The response omits notes fields once cleared, so pass them explicitly.
      const fields = {
        notes: res?.notes || '',
        notes_updated_at: res?.notes_updated_at || '',
        notes_updated_by: res?.notes_updated_by || '',
      };
      setDraft(fields.notes);
      if (onSaved) await onSaved(fields);
      enqueueSnackbar(notes ? 'Notes saved' : 'Notes cleared', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(err?.message || 'Failed to save notes', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const meta = deviceHasNotes(device) && (device.notes_updated_by || device.notes_updated_at) && (
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
      Updated
      {device.notes_updated_by ? ` by ${device.notes_updated_by}` : ''}
      {device.notes_updated_at ? ` · ${new Date(device.notes_updated_at).toLocaleString()}` : ''}
    </Typography>
  );

  const saveButton = (
    <Button
      variant="contained"
      size="small"
      disabled={saving || draft.trim() === deviceNotes(device)}
      onClick={(e) => {
        stop(e);
        save(draft);
      }}
      sx={buttonSx ? { ...buttonSx, flexShrink: 0 } : { textTransform: 'none', minWidth: 56, flexShrink: 0 }}
    >
      {saving ? 'Saving…' : 'Save'}
    </Button>
  );

  if (!canEdit) {
    return (
      <Box>
        {label}
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }} color={deviceHasNotes(device) ? 'text.primary' : 'text.secondary'}>
          {deviceHasNotes(device) ? deviceNotes(device) : 'No notes'}
        </Typography>
        {meta}
      </Box>
    );
  }

  return (
    <Box>
      {label ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>{label}</Box>
          {saveButton}
        </Box>
      ) : null}
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={minRows}
        maxRows={maxRows}
        placeholder="Add a note for this lamp"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={saving}
        onClick={stop}
        onKeyDown={stop}
        inputProps={{ maxLength: 2000 }}
      />
      {(meta || deviceHasNotes(device) || !label) && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>{meta}</Box>
        {deviceHasNotes(device) && (
          <Button
            variant="outlined"
            color="warning"
            size="small"
            disabled={saving}
            onClick={(e) => {
              stop(e);
              save('');
            }}
            sx={buttonSx || { textTransform: 'none', fontSize: '0.75rem' }}
          >
            Clear
          </Button>
        )}
        {label ? null : saveButton}
      </Box>
      )}
    </Box>
  );
};

export default DeviceNotesEditor;
